/**
 * Atalho: de um pagamento não identificado para um filtro da tela de Títulos.
 *
 * O trabalho que isto encurta é o de conciliação: alguém olha um pagamento que entrou, não sabe a
 * que título ele pertence, e sai procurando pelo valor, pela data ou pelo pagador. Cada uma dessas
 * buscas é um filtro que a tela já sabe fazer — o que faltava era o caminho de um lado ao outro.
 *
 * Mapeamento (combinado com quem opera a tela):
 *
 *   Data          → Vencimento, entre datas, o dia do pagamento nas duas pontas
 *   Valor         → Valor, entre, o valor nas duas pontas
 *   Nome Pagador  → Pagador Nome, contém
 *   Doc. Pagador  → Pagador Doc., é (exato)
 *
 * O MEIO (PIX, boleto…) não vira filtro: ele não estreita nada útil — quase todo pagamento é PIX,
 * e a tela de Títulos não guarda por qual meio um título será pago.
 */
import { AppliedFilter } from '../../../molecules/FilterBuilder/types';
import { PaymentRow } from '../services/billingApi';

/** As colunas que oferecem o atalho. `paymentMethod` de fora é intencional (ver acima). */
export type ShortcutField = 'date' | 'amount' | 'payerName' | 'payerDocument';

/**
 * O instante UTC do pagamento no dia do CALENDÁRIO LOCAL, em ISO.
 *
 * `toISOString().slice(0,10)` daria o dia em UTC: um PIX das 22h em São Paulo viraria o dia
 * seguinte, e o filtro procuraria vencimentos na data errada — o mesmo erro que o `tz` da tela de
 * Pagamentos existe para evitar.
 */
export const localDayISO = (iso: string | null): string | null => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
};

/** Rótulo humano do que vai ser filtrado — o dialog de confirmação mostra isto. */
export const shortcutDescription = (field: ShortcutField): string => ({
    date: 'Vencimento na data deste pagamento',
    amount: 'Valor igual ao deste pagamento',
    payerName: 'Pagador do Título com este nome',
    payerDocument: 'Pagador do Título com este documento',
}[field]);

/**
 * O filtro correspondente, ou `null` quando o pagamento não tem aquele dado.
 *
 * Devolver `null` em vez de um filtro vazio é deliberado: filtro sem valor não estreita nada e
 * ainda aparece no painel como se estivesse valendo — o usuário concluiria que a busca já
 * considerou o pagador quando ela não considerou.
 */
export const filterFromPayment = (
    payment: PaymentRow, field: ShortcutField,
): AppliedFilter | null => {
    const base = { id: `atalho_${field}_${payment.paymentId}` };

    if (field === 'date') {
        const day = localDayISO(payment.paymentDate);
        if (!day) return null;
        // as duas pontas no mesmo dia: o operador da tela é "entre datas", e é assim que se
        // expressa "exatamente neste dia" sem inventar um operador novo
        return {
            ...base, key: 'due_date', label: 'Vencimento', type: 'date',
            operator: 'between_dates', value: [day, day]
        };
    }

    if (field === 'amount') {
        if (payment.amount == null) return null;
        const amount = String(payment.amount);
        return {
            ...base, key: 'amount', label: 'Valor', type: 'number',
            operator: 'between', value: [amount, amount]
        };
    }

    if (field === 'payerName') {
        const nome = payment.payer?.name?.trim();
        if (!nome) return null;
        return {
            ...base, key: 'title_payer_name', label: 'Pagador do Título Nome',
            type: 'text', operator: 'contains', value: nome
        };
    }

    const doc = payment.payer?.documentNumber?.trim();
    if (!doc) return null;
    // `eq` e não `contains`: documento é chave, e busca parcial traria outro CNPJ que só
    // compartilha dígitos — ruído justamente onde se quer precisão
    return {
        ...base, key: 'title_payer_document', label: 'Pagador do Título Doc.',
        type: 'text', operator: 'eq', value: doc
    };
};

/**
 * Junta o filtro novo aos que já valiam, SUBSTITUINDO o de mesma chave.
 *
 * Acumular dois filtros da mesma coluna não estreita, contradiz: "valor entre 100 e 100" mais
 * "valor entre 500 e 500" não devolve nada. Trocar é o que o usuário quer dizer ao acrescentar um
 * atalho sobre uma coluna que ele já havia filtrado.
 */
export const addFilter = (
    atuais: AppliedFilter[], nextFilter: AppliedFilter,
): AppliedFilter[] => [...atuais.filter(f => f.key !== nextFilter.key), nextFilter];
